import { useState } from 'react';
import { Drawer, Skeleton } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { MetaChip, StatusPill, TextArea } from '../../components';
import { useAddContactNote, useContactContextQuery } from '../../queries/messages';
import { P, fs, eyebrow } from '../../theme/tokens';
import { formatPhone } from '../../utils/callLog';
import { CATEGORY_META, ROLE_META } from './constants';
import { PrimaryBtn } from './styled';

const Name = styled.h2`
  margin: 0;
  font-size: ${fs.lg}px;
  font-weight: 700;
  color: ${P.ink};
`;

const Sub = styled.div`
  margin-top: 4px;
  font-size: ${fs.xs}px;
  color: ${P.subtle};
`;

const Info = styled.dl`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 10px 12px;
  margin: 18px 0;
  font-size: ${fs.sm}px;

  dt {
    ${eyebrow}
    padding-top: 2px;
  }
  dd {
    margin: 0;
    color: ${P.ink};
    word-break: break-word;
  }
`;

const Section = styled.section`
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid ${P.line};

  h3 {
    display: flex;
    justify-content: space-between;
    ${eyebrow}
    margin: 0 0 10px;
  }
`;

const Notes = styled.p`
  margin: 0 0 4px;
  white-space: pre-wrap;
  font-size: ${fs.sm}px;
  color: ${(p) => (p.$empty ? P.subtle : P.ink)};
  line-height: 1.55;
`;

const Events = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li > div {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }
  time {
    font-size: 11px;
    color: ${P.subtle};
    white-space: nowrap;
  }
  p {
    margin: 5px 0 0;
    font-size: ${fs.xs}px;
    color: ${P.muted};
    line-height: 1.5;
  }
`;

function NoteForm({ contactNumber, ctx }) {
  const [note, setNote] = useState('');
  const addNote = useAddContactNote(contactNumber);

  const save = () => {
    const line = `${dayjs().format('MMM D, h:mm A')} · ${note.trim()}`;
    const notes = ctx.notes ? `${ctx.notes}\n${line}` : line;
    addNote.mutate({ clientId: ctx.linkedRecord.id, notes }, { onSuccess: () => setNote('') });
  };

  return (
    <>
      <TextArea
        style={{ marginTop: 10, minHeight: 72 }}
        aria-label="New note"
        placeholder="Add a note to this record…"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <PrimaryBtn
        type="button"
        style={{ width: '100%', marginTop: 8 }}
        onClick={save}
        disabled={!note.trim() || addNote.isPending}
      >
        Add note
      </PrimaryBtn>
    </>
  );
}

function ContactDetail({ contactNumber, ctx }) {
  const role = ROLE_META[ctx.type] || ROLE_META.contact;
  const noteCount = ctx.notes ? ctx.notes.split('\n').filter(Boolean).length : 0;
  const history = ctx.history || [];

  return (
    <>
      <Name>{ctx.name || formatPhone(contactNumber)}</Name>
      <Sub>{history[0] ? `Last activity ${dayjs(history[0].at).format('MMM D')}` : 'No recent activity'}</Sub>
      <div style={{ marginTop: 12 }}>
        <StatusPill color={role.color}>{role.label}</StatusPill>
      </div>

      <Info>
        <dt>Phone</dt>
        <dd>{formatPhone(ctx.phone || contactNumber)}</dd>
        {ctx.email && (
          <>
            <dt>Email</dt>
            <dd>{ctx.email}</dd>
          </>
        )}
        <dt>Language</dt>
        <dd>{ctx.language || 'English'}</dd>
        {ctx.property && (
          <>
            <dt>Property</dt>
            <dd>{ctx.property}</dd>
          </>
        )}
        {ctx.assignedTo && (
          <>
            <dt>Assigned</dt>
            <dd>{ctx.assignedTo}</dd>
          </>
        )}
      </Info>

      <Section>
        <h3>
          <span>Notes</span>
          <span>{noteCount}</span>
        </h3>
        <Notes $empty={!ctx.notes}>{ctx.notes || 'No notes yet. Anything you add stays with this record.'}</Notes>
        {ctx.linkedRecord && <NoteForm contactNumber={contactNumber} ctx={ctx} />}
      </Section>

      <Section>
        <h3>Communication history</h3>
        {history.length ? (
          <Events>
            {history.map((h) => (
              <li key={`${h.kind}-${h.at}-${h.label}`}>
                <div>
                  <MetaChip color={(CATEGORY_META[h.kind] || CATEGORY_META.general).color}>{h.label}</MetaChip>
                  <time dateTime={h.at}>{dayjs(h.at).format('MMM D, h:mm A')}</time>
                </div>
                <p>{h.text}</p>
              </li>
            ))}
          </Events>
        ) : (
          <Notes $empty>No communication history yet.</Notes>
        )}
      </Section>
    </>
  );
}

export default function ContactDrawer({ open, contactNumber, onClose }) {
  const { data: ctx, isLoading, isError } = useContactContextQuery(contactNumber, open);

  return (
    <Drawer open={open} onClose={onClose} width={400} title="Contact profile">
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : isError || !ctx ? (
        <Notes $empty>Could not load this contact.</Notes>
      ) : (
        <ContactDetail contactNumber={contactNumber} ctx={ctx} />
      )}
    </Drawer>
  );
}
